import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { addMapMode } from 'client/store/mapSlice/mapSlice';
import { setDrawType } from 'client/store/drawSlice/drawSlice';
import { setAlertProps } from 'client/store/alertSlice/alertSlice';
import PropTypes from 'prop-types';
import Button, { BUTTON_VARIANTS } from 'client/components/aria/Button/Button';
import { BOX, CIRCLE, POLYGON, LINE } from 'client/utils/constants/symbolName';
import { mapModes } from 'client/utils/constants/appStates';
import { VALID_MODES } from './DrawButton.mode';
import styles from './DrawButton.module.scss';
import { permissions } from 'client/utils/constants/permissions';
import { useHasUserPermission } from 'client/utils/auth/userPermissions';
import mapModeIsValid from '../validateModes';
import { DESKTOP } from 'client/utils/constants/GDS/js/devices';
import useDeviceType from 'client/utils/helpers/DeviceType/useDeviceType';
import { useMapScale } from 'client/pages/MapPage/hooks/useMapScale';
import { MAX_SCALE } from 'client/pages/MapPage/layers/constants';
import { useClearAOI } from 'client/pages/MapPage/hooks/clearAOI';
import { useCallback } from 'react';
import { LINE_STRING } from 'client/utils/constants/geometryType';
import DrawSquareIcon from './Icons/DrawAreaIcon';
import DrawLineIcon from './Icons/DrawLineIcon';
import DrawCustomAreaIcon from './Icons/DrawCustomAreaIcon';

const { DRAWING, LOADING, MODIFYING } = mapModes;

const DRAW_BUTTON_CONFIG = {
    [BOX]: {
        labelId: 'mapToolbar.drawButton.box',
        Icon: DrawSquareIcon
    },
    [POLYGON]: {
        labelId: 'mapToolbar.drawButton.polygon',
        Icon: DrawCustomAreaIcon
    },
    [LINE_STRING]: {
        labelId: 'mapToolbar.drawButton.line',
        Icon: DrawLineIcon
    }
};

const DrawButton = ({
    disableAboveMaxScale,
    type,
    variant,
    setCreateMobileAOIDialog,
    onAfterDraw,
    className,
    onPress,
    disabled,
    ...props
}) => {
    const dispatch = useDispatch();
    const mapMode = useSelector(state => state.map?.mode || []);
    const drawStatus = useSelector(state => state.draw?.status || []);
    const deviceType = useDeviceType();

    const scale = useMapScale();
    const clearAOI = useClearAOI();
    const hasDrawPermission = useHasUserPermission(permissions.AOI_DRAW);
    const hasLineDrawPermission = useHasUserPermission(permissions.AOI_DRAW_LINE);
    const canShow =
    (
        (hasDrawPermission && type !== LINE_STRING) ||
        (hasLineDrawPermission && type === LINE_STRING)
    ) &&
     mapModeIsValid(mapMode, VALID_MODES);

    const config = DRAW_BUTTON_CONFIG[type];

    const isDisabledAboveMaxScale = disableAboveMaxScale && scale > MAX_SCALE;
    const isActivelyEditingAoi = mapMode.includes(DRAWING) || mapMode.includes(MODIFYING);
    const isBusy = isActivelyEditingAoi || mapMode.includes(LOADING) || (drawStatus.length > 0 && isActivelyEditingAoi);
    const isDisabled = Boolean(isDisabledAboveMaxScale || isBusy || disabled);

    const handleDraw = useCallback(() => {
        clearAOI();
        dispatch(addMapMode(DRAWING));
        if (deviceType === DESKTOP) {
            dispatch(setDrawType(type));
            dispatch(setAlertProps({
                open: true,
                severity: 'info',
                content: <FormattedMessage id='mapToolbar.startDrawing' />
            }));
        } else {
            setCreateMobileAOIDialog(true);
        }

        onAfterDraw();
    }, [clearAOI, dispatch, deviceType, setCreateMobileAOIDialog, onAfterDraw, type]);

    const handlePress = useCallback((event) => {
        handleDraw();
        onPress?.(event);
    }, [handleDraw, onPress]);

    if (!canShow || !config) {
        return null;
    }

    const buttonText = <FormattedMessage id={config.labelId} />;
    const buttonIcon = (
        <config.Icon className={styles.icon} aria-hidden="true" focusable="false" />
    );

    // return null;
    return (
        <Button
            variant={variant}
            className={[styles.drawButton, className].filter(Boolean).join(' ')}
            elementType="button"
            onPress={handlePress}
            isDisabled={isDisabled}
            {...props}
        >
            {buttonIcon}
            {buttonText}
        </Button>
    );
};

DrawButton.propTypes = {
    disableAboveMaxScale: PropTypes.bool,
    setCreateMobileAOIDialog: PropTypes.func,
    onAfterDraw: PropTypes.func,
    variant: PropTypes.oneOf(BUTTON_VARIANTS),
    className: PropTypes.string,
    onPress: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf([POLYGON, CIRCLE, BOX, LINE]).isRequired
};

DrawButton.defaultProps = {
    disableAboveMaxScale: false,
    variant: 'info',
    setCreateMobileAOIDialog: () => {},
    onAfterDraw: () => {}
};

export default DrawButton;
